import {
  contrastRatioFromLuminance,
  relativeLuminance,
  targetLuminanceFor,
} from './color-contrast.utils';
import {
  Oklch,
  oklchToSrgb,
  parseCssColorToRgb,
  Rgb,
  rgbToHex,
  srgbToOklch,
} from './color-space.utils';

/**
 * Adapts backend-authored colours for dark mode.
 *
 * ── The problem ───────────────────────────────────────────────────────
 * Every colour the backend sends (chart series, table badges, progress bars,
 * SimpleTable row tints) was picked to look right on WHITE. Dropped unchanged
 * onto a #1A1A1A surface, the dark ones vanish: #171717 goes from 17.9:1
 * against white to 1.03:1 against the card.
 *
 * ── What we do NOT do ─────────────────────────────────────────────────
 * The intuitive fix — solve for the colour whose contrast against the dark
 * surface EQUALS its old contrast against white — is wrong, and measurably so.
 * Contrast against a near-black surface grows with lightness, so reproducing a
 * LOW light-mode ratio demands a DARK colour: the brand teal #4DC0B9 (2.20:1
 * on white) would come out as #005754. That is exactly the "just make it
 * darker" outcome we are trying to avoid.
 *
 * Two cheaper heuristics were also measured and rejected:
 *   • OKLCH lightness mirroring (L' = 1 - L) darkens every brand colour and
 *     destroys contrast (#F5A623 → 1.05:1, invisible).
 *   • Clamping L into [0.55, 0.85] flattens everything to ~3.5:1, erasing the
 *     emphasis hierarchy the backend encodes.
 *
 * ── What we do ────────────────────────────────────────────────────────
 * Treat the old contrast as a FLOOR and only ever lighten:
 *
 *   1. If the colour already has at least its old contrast against the dark
 *      surface, return it byte-identical. Roughly half of real palettes take
 *      this branch, so most charts do not shift at all.
 *   2. Otherwise hold hue and chroma and raise OKLCH lightness by bisection
 *      until the floor is met, then gamut-map back to sRGB.
 *
 * Never darkens, never loses contrast, hue is preserved exactly. Chroma is
 * only given up where sRGB has none left near white (a very dark navy becomes
 * a pale blue) — the same trade Material's tonal palettes make.
 *
 * Light mode is the identity for the `ink` and `fill` roles, so the default
 * theme costs nothing: the solver is never entered.
 */

/**
 * How a backend colour is being used. The distinction matters because
 * lightening is right for a mark and catastrophic for an area behind text —
 * a lightened row background in dark mode is a blinding stripe.
 */
export type ColorRole =
  /** A mark drawn ON the surface: bars, lines, points, gauge arcs, dots, progress fills. */
  | 'ink'
  /** An area behind text that light mode paints verbatim (SimpleTable RowTint rows). */
  | 'fill'
  /** An area behind text that light mode already softens (pill / badge chips). */
  | 'soft';

export const COLOR_SURFACES = {
  /** The surface backend colours were authored against. */
  light: '#FFFFFF',
  /** Must stay in sync with `--mj-surface-1` in tokens/palette-dark.less. */
  dark: '#1A1A1A',
  /** Alpha for the `soft` role in light mode — matches the previous 12% mix. */
  softAlphaLight: 0.12,
  /** Alpha for `fill`/`soft` in dark mode. Higher: a 12% wash on #1A1A1A is invisible. */
  tintAlphaDark: 0.2,
} as const;

/** Bisection steps on OKLCH lightness. 24 lands well below one 8-bit step. */
const LIGHTNESS_STEPS = 24;

/**
 * Adaptation is pure and its inputs repeat heavily — a 500-cell table draws
 * from a handful of distinct colours — so results are memoised for the
 * process lifetime. The cap only exists so a pathological payload with a
 * unique colour per row cannot grow this without bound.
 */
const MAX_CACHE_ENTRIES = 4096;
const cache = new Map<string, string>();

const LIGHT_SURFACE_RGB = parseCssColorToRgb(COLOR_SURFACES.light) ?? { r: 255, g: 255, b: 255 };
const DARK_SURFACE_RGB = parseCssColorToRgb(COLOR_SURFACES.dark) ?? { r: 26, g: 26, b: 26 };
const LIGHT_SURFACE_LUMINANCE = relativeLuminance(LIGHT_SURFACE_RGB);
const DARK_SURFACE_LUMINANCE = relativeLuminance(DARK_SURFACE_RGB);

/**
 * Adapts a backend colour for the active theme.
 *
 * Never throws: an unparseable value is returned verbatim, because this runs
 * in render paths fed by data we do not control.
 */
export function adaptColor(
  color: string,
  isDark: boolean,
  role: ColorRole = 'ink',
): string {
  // The wire types say these fields are required, but nothing validates the
  // payload at runtime and this is called straight from templates — a missing
  // field must degrade, not throw during change detection.
  if (typeof color !== 'string' || color === '') return '';

  const key = `${role}|${isDark ? 'd' : 'l'}|${color.trim().toLowerCase()}`;
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  const result = computeAdaptedColor(color, isDark, role);

  if (cache.size >= MAX_CACHE_ENTRIES) cache.clear();
  cache.set(key, result);
  return result;
}

/** `adaptColor` for the nullable wire fields. */
export function adaptColorOrNull(
  color: string | null | undefined,
  isDark: boolean,
  role: ColorRole = 'ink',
): string | null {
  if (color === null || color === undefined || color.trim() === '') return null;
  return adaptColor(color, isDark, role);
}

/** `rgba()` version of `color`, or the input verbatim if it cannot be parsed. */
export function withAlpha(color: string, alpha: number): string {
  const rgb = parseCssColorToRgb(color);
  if (!rgb) return color;
  const normalizedAlpha = Math.max(0, Math.min(1, alpha));
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${normalizedAlpha})`;
}

/**
 * Blends `color` toward `target` by `ratio` (0 = untouched, 1 = target).
 *
 * Replaces a component-private `mixTowardWhite`. Hardcoding white was a latent
 * dark-mode bug: fading a gradient toward white on a dark surface makes the
 * faded end MORE prominent, inverting the intended de-emphasis. Callers pass
 * the actual surface instead.
 */
export function mixToward(color: string, target: string, ratio: number): string {
  const from = parseCssColorToRgb(color);
  const to = parseCssColorToRgb(target);
  if (!from || !to) return color;

  const amount = Math.max(0, Math.min(1, ratio));
  return rgbToHex({
    r: Math.round(from.r + (to.r - from.r) * amount),
    g: Math.round(from.g + (to.g - from.g) * amount),
    b: Math.round(from.b + (to.b - from.b) * amount),
  });
}

/** The surface backend colours are composited over in the given theme. */
export function surfaceFor(isDark: boolean): string {
  return isDark ? COLOR_SURFACES.dark : COLOR_SURFACES.light;
}

/**
 * Whichever of `preferred` / `alternative` has more contrast against
 * `background`. Ties and unparseable input keep `preferred`.
 *
 * Needed where a solid backend-coloured fill carries ink: dark mode LIGHTENS
 * the fill, so ink that was safe on the original colour can stop being safe.
 * Callers that must not change light mode should gate this on `isDark`.
 */
export function pickReadableInk(
  background: string,
  preferred: string,
  alternative: string,
): string {
  const bg = parseCssColorToRgb(background);
  const a = parseCssColorToRgb(preferred);
  const b = parseCssColorToRgb(alternative);
  if (!bg || !a || !b) return preferred;

  const backgroundLuminance = relativeLuminance(bg);
  const preferredContrast = contrastRatioFromLuminance(
    relativeLuminance(a),
    backgroundLuminance,
  );
  const alternativeContrast = contrastRatioFromLuminance(
    relativeLuminance(b),
    backgroundLuminance,
  );
  return alternativeContrast > preferredContrast ? alternative : preferred;
}

/** Test hook. The cache is otherwise process-lifetime. */
export function clearColorAdaptCache(): void {
  cache.clear();
}

function computeAdaptedColor(
  color: string,
  isDark: boolean,
  role: ColorRole,
): string {
  if (!isDark) {
    return role === 'soft' ? softWash(color, COLOR_SURFACES.softAlphaLight) : color;
  }

  const ink = adaptInkForDark(color);
  return role === 'ink' ? ink : softWash(ink, COLOR_SURFACES.tintAlphaDark);
}

/**
 * Alpha wash of `color`.
 *
 * Falls back to `color-mix()` rather than `withAlpha`'s pass-through when the
 * colour cannot be parsed: pass-through would return the value OPAQUE, so a
 * chip would paint its own text colour as its background. `color-mix` accepts
 * every CSS colour syntax (named colours, `hsl()`, `#RRGGBBAA`, `color()`),
 * which is what the `color-mix(in srgb, <c> 12%, white)` this replaced did.
 */
function softWash(color: string, alpha: number): string {
  if (parseCssColorToRgb(color) !== null) return withAlpha(color, alpha);
  return `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, transparent)`;
}

function adaptInkForDark(color: string): string {
  const rgb = parseCssColorToRgb(color);
  if (!rgb) return color;

  const luminance = relativeLuminance(rgb);
  const contrastOnLight = contrastRatioFromLuminance(luminance, LIGHT_SURFACE_LUMINANCE);
  const contrastOnDark = contrastRatioFromLuminance(luminance, DARK_SURFACE_LUMINANCE);

  // Already at least as legible as it was on white — leave it exactly alone.
  if (contrastOnDark >= contrastOnLight) return color;

  const targetLuminance = targetLuminanceFor(contrastOnLight, DARK_SURFACE_LUMINANCE);
  const source = srgbToOklch(rgb);

  return rgbToHex(solveForLuminance(source, targetLuminance));
}

/**
 * Smallest lightness in `[source.l, 1]` whose gamut-mapped colour reaches
 * `targetLuminance`, holding chroma and hue.
 *
 * Bisection returns the UPPER bound so the result meets or exceeds the target
 * rather than landing a hair under it. If the target is unreachable — pure
 * black wants 21:1 but white only yields 17.40:1 on #1A1A1A — this settles at
 * L=1, which is the closest legal answer.
 */
function solveForLuminance(source: Oklch, targetLuminance: number): Rgb {
  let low = source.l;
  let high = 1;

  for (let step = 0; step < LIGHTNESS_STEPS; step += 1) {
    const mid = (low + high) / 2;
    const candidate = oklchToSrgb({ l: mid, c: source.c, h: source.h });
    if (relativeLuminance(candidate) >= targetLuminance) {
      high = mid;
    } else {
      low = mid;
    }
  }

  return oklchToSrgb({ l: high, c: source.c, h: source.h });
}
